// Use https or http.get or whatever you need for your platform
// This example uses Axios which is server / browser compatible
// https://www.npmjs.com/package/axios
import axios, { AxiosInstance } from 'axios';

// Library for Events that are strongly typed
// see https://github.com/vitaly-t/sub-events
import { SubEvent } from 'sub-events';

// Local JSON Management tools
import { DataTools } from './DataTools';
import { LogicEngine } from '../LogicEngine/LogicEngine';

// SHA1 hash function that works on server and browser
import sha1 from 'sha1';

/*npm in
Import known data types from the shared platform
*/
import { Site } from '../types/Site';
import { EDCPortalConfig } from '../types/EDCPortalConfig';
import { Sidebar } from '../types/Sidebar';
import { DataDictionaryType } from '../types/DataDictionaryType';
import { PageMobileConfigType } from '../PageParser/PageTypes';

import { ApiCallResult } from '../types/ApiCallResult';

/* Import wrapper class for data dictionary */
import { DataDictionary } from './DataDictionary';

/* Import test data for development testing */
import TestData from '../test_data/TestData';
import { User } from '../types/User';

/** Abstract Data Manager Class */
import { Role } from '../types/Role';
import { Group } from '../types/Group';
import { Membership } from '../types/Membership';
import { AuthResult } from '../types/AuthResult';
import { ChangePasswordResult } from '../types/ChangePasswordResult';
import { PingResult } from '../types/PingResult';
import { PingHubResult } from '../types/PingHubResult';
import { ValidateParticipantResult } from '../types/ValidateParticipantResult';
import { ParticipantAuthResult } from '../types/ParticipantAuthResult';
import { GenericResult } from '../types/GenericResult';
import { ParticipantRegisterResult } from '../types/ParticipantRegisterResult';
import { ValidateUserResult } from '../types/ValidateUserResult';
import { ParticipantSendMessageResult } from '../types/ParticipantSendMessageResult';
import { SiteSendMessageResult } from '../types/SiteSendMessageResult';
import { ParticipantSendEmailResult } from '../types/ParticipantEmailMessageResult';
import { StampPdfResult } from '../types/StampPdfResult';
import { AttachmentManager } from './AttachmentManager';
import { AttachmentSaveResult } from '../types/AttachmentSaveResult';
import { DataRecord } from './DataRecord';
import { Participant } from '../Participant/Participant';
import { TaskManager } from '../Tasks/TaskManager';
import { DataQuery } from './DataQuery';
import { ParticipantSendSmsResult } from '../types/ParticipantSendSmsResult';

const FormData = require('form-data');

// Define the current customer that we are using for this project.
// May be flexible in the future, can be defined this way at the time.
const globalCurrentProject = 'RB1';

// Set a debug level for api calls, 0=None, 1=Request, 2=Request+Response
const globalDebugApiCalls: number = 0;

const globalUseProxy: boolean = false;

type HttpMethods = 'get' | 'post' | 'put' | 'patch' | 'delete';

type Environment = 'test' | 'dev' | 'uat' | 'stg' | 'prod';
export { Environment };

export interface PdfStampParameter
{
	sourceDocument: string;
	pageTitle: string;
	participant_id: string;
	consentText: string;
	lines: string[][];
}

export interface SystemProblemEventType
{
	// Defines the type of event and the text message that may need
	// to be translated and displayed.  Currently event_text is a placeholder
	// For "server_data", this happens when trying to save a record to the server
	// and the server validations or data dictionary has a problem.   Client
	// should use for debugging or possibly display some "unknown error" message box.
	event_type: 'token' | 'connection' | 'server_data';
	event_text: string;

	// If true, the mobile app or client should return to the login screen.  Something
	// is wrong and a new login is needed.
	// Currently triggered when the API calls have a problem.
	should_relogin: boolean;

	// If true, something in the display needs to be reload.
	// Currently not triggered, placeholder may be used soon.
	should_refresh_dispaly: boolean;
}

/**

 This module provides data from the server and sends data to the server.
 Note the use of strong types, tsdoc comments, and tslint valid syntax.

 Access to the CDN (Content Delivery Network) Folder with edit access:
 http://protovate.sharepoint.com/sites/LainaGeneral/cdn

 @category Data and API Services

 */
export class DataServices
{
	static _serverAddress: string | undefined = undefined;

	/**
	 Should be a single instance access function so that we can share common references without
	 having to reload configuration files more than once
	 */
	static _instance: DataServices;

	/**
	 * An updated indicator that shows if we are online of offline
	 * TODO Should be checked using a timer or event of some type
	 */
	static _isOnline: boolean = true;

	/**
	 * List of database managers that are used to get local updates,
	 * save a copy of local updates, etc.  T
	 */
	static _environment: Environment = 'dev';

	/**
	 * Store the current customer that we are using for this project.
	 */
	static _currentProject: string = 'RB1';

	/** Debugging Hoook - See Demo/SpeedTest for usage */
	static debugAxiosCreate: (i: AxiosInstance) => void = (i) => { };

	/**
	 * Event that is fired when certain aspects of data may need to be refreshed
	 * due to a record being added.
	 */
	static evSystemProblem: SubEvent<SystemProblemEventType> = new SubEvent();

	/**
	 Store a reference to the promise or observable that will be returned from a function.
	 This way, future calls will get the same result without having to re-request the
	 data.  This applies to data that will not change over time.
	 */
	public promiseGetSites?: Promise<Site[]>;
	public promiseGetDataDictionary?: Promise<DataDictionary>;
	public promiseGetUser?: Promise<User[]>;
	public promiseGetRole?: Promise<Role[]>;
	public promiseGetGroup?: Promise<Group[]>;
	public promiseGetMembership?: Promise<Membership[]>;

	public promiseGetEdcConfig?: EDCPortalConfig;
	public promiseGetMobileConfig?: PageMobileConfigType;
	public promiseTaskConfig?: PageMobileConfigType;

	public authToken: string | undefined = undefined;

	// Cached copy of the mobile config and other config files
	private cacheEdcPages: { [filename: string]: EDCPortalConfig } = {};

	/**
	 * Short term in memory cache
	 */
	_recentCache: { [hashKey: string]: any } = {};

	/**
	 * Set server address
	 * @param baseAddress A string value to set server address
	 */
	static setServerAddress(baseAddress: string)
	{
		DataServices._serverAddress = baseAddress;
	}

	/**
	 * Get data service instance
	 * @returns instance for data service
	 */
	static instance(): DataServices
	{
		if (DataServices._instance) return DataServices._instance;
		DataServices._instance = new DataServices();
		return DataServices._instance;
	}

	constructor() { }

	/** Set define information for logging purposes */
	public static setDeviceId(device_info: string)
	{
		LogicEngine.currentDeviceId = device_info;
	}

	/** Assign the environment */
	public static setEnvironment(environment: Environment)
	{
		DataServices._environment = environment;
	}

	/** Get the environment
	 * @returns environment
	 */
	public static getEnvironment(): Environment
	{
		return DataServices._environment;
	}

	/** Assign the project name */
	public static setProjectName(projectName: string)
	{
		DataServices._currentProject = projectName;
	}

	/** Get the project name
	 * @returns project name
	 */
	public static getProjectName(): string
	{
		return DataServices._currentProject;
	}

	/** Central Hub Server address */
	private internalGetHubServer(): string
	{
		return `https://${DataServices._environment}.hub.lainaent.com/`;
	}

	/**
	 * Returns the server base address based on the trial and configured customer
	 * and development mode.
	 */
	private internalGetServerBase(): string
	{
		if (DataServices._serverAddress) return DataServices._serverAddress;

		if (DataServices._environment === 'test')
			return 'http://localhost:3000/';

		if (DataServices._environment === 'dev')
			return 'https://dev.' + DataServices._currentProject + '.trial.lainaent.com/';

		if (DataServices._environment === 'uat')
			return 'https://uat.' + DataServices._currentProject + '.trial.lainaent.com/';

		if (DataServices._environment === 'stg')
			return 'https://stg.' + DataServices._currentProject + '.trial.lainaent.com/';

		if (DataServices._environment === 'prod')
			return 'https://prod.' + DataServices._currentProject + '.trial.lainaent.com/';

		return 'https://stg.' + DataServices._currentProject + '.trial.lainaent.com/';
	}

	/**
	 * Returns the server base address based on the trial and configured customer
	 * and development mode.
	 */
	private internalGetAxios(useHub: boolean = false): AxiosInstance
	{
		const server = useHub ? this.internalGetHubServer() : this.internalGetServerBase();

		const axiosOptions: any = {
			baseURL: server,
			timeout: 2000000,
			headers: {},
		};

		if (this.authToken)
		{
			axiosOptions.headers['x-auth-token'] = this.authToken;
		}

		if (globalUseProxy)
		{
			axiosOptions.proxy = {
				host: '127.0.0.1',
				port: 8888,
			};
		}

		let i = axios.create(axiosOptions);
		DataServices.debugAxiosCreate(i);
		return i;
	}

	/**
	 Internal function that returns the base URI for accessing the
	 content delivery network.

	 @returns The string URI
	 */
	internalGetCdnBase(): string
	{
		return this.internalGetServerBase() + 'cdn/';
	}

	/**
	 * Returns the current status of our connection to the server.
	 * @returns true if the client is connected to the internet as best as we can tell.
	 */
	public getOnlineConnected(): boolean
	{
		return DataServices._isOnline;
	}

	/**
	 Get a list of sites for this trial
	 @returns A promise for a list of sites
	 */
	public async getTrialSites(): Promise<Site[]>
	{
		if (this.promiseGetSites) return this.promiseGetSites as Promise<Site[]>;

		this.promiseGetSites = (async (): Promise<Site[]> =>
		{
			let result: Site[] | null = await this.internalGetServerFile<Site[]>('Sites.json');

			if (!result)
			{
				// TODO: Handle critical situation if missing site data.
				return [];
			}

			return result;
		})();

		return this.promiseGetSites;
	}

	/**
	 Return EDC Portal Config
	 @returns A promise for the EDC Portal Config
	 */
	public async getEDCPortalConfig(): Promise<EDCPortalConfig>
	{
		if (this.promiseGetEdcConfig) return this.promiseGetEdcConfig;

		let result: EDCPortalConfig | null = await this.internalGetServerFile<EDCPortalConfig>(
			'EDC Portal.json',
		);

		if (!result)
		{
			// TODO: Handle critical situation if missing site data.
			return {
				Sidebar: [],
				worksheets: [],
				tables: {},
			};
		}

		if (result.tables['Sidebar'])
		{
			result.Sidebar = result.tables['Sidebar'][0].data as unknown as Sidebar[];
		}

		this.promiseGetEdcConfig = result;
		return this.promiseGetEdcConfig;
	}

	/** Get reference to SystemConfig which holds pages for System
	 * @returns A Promise for the EDC Portal Config
	 */
	public async getSystemPages(): Promise<EDCPortalConfig>
	{
		let result: EDCPortalConfig | null = await this.internalGetServerFile<EDCPortalConfig>(
			'SystemPages.json',
		);

		if (!result)
		{
			// TODO: Handle critical situation if missing site data.
			return {
				Sidebar: [],
				worksheets: [],
				tables: {},
			};
		}

		return result;
	}

	/** Return Task Config
	 * @returns A promise for the Task Config
	 */
	public async getTaskConfig(): Promise<PageMobileConfigType>
	{
		if (this.promiseTaskConfig) return this.promiseTaskConfig;

		let result: PageMobileConfigType | null =
			await this.internalGetServerFile<PageMobileConfigType>('TaskConfiguration.json');

		if (!result)
		{
			// TODO: Handle critical situation if missing site data.
			return {
				worksheets: [],
				tables: {},
			};
		}

		this.promiseTaskConfig = result;
		return result;
	}

	/**
	 Return Mobile Config
	 @returns A promise for the Mobile Config
	 */
	public async getMobileConfig(filename: string): Promise<EDCPortalConfig>
	{
		if (this.cacheEdcPages[filename]) return this.cacheEdcPages[filename];

		let result: EDCPortalConfig | null = await this.internalGetServerFile<EDCPortalConfig>(
			filename,
		);

		if (!result)
		{
			// TODO: Handle critical situation if missing site data.
			return {
				worksheets: [],
				Sidebar: [],
				tables: {},
			};
		}

		if (result.tables && result.tables['Sidebar'])
		{
			result.Sidebar = result.tables['Sidebar'][0].data as unknown as Sidebar[];
		}

		this.cacheEdcPages[filename] = result;
		return result;
	}

	/**
	 Gets the master data dictionary file
	 @returns A promise for the master data dictionary
	 */
	public async getDataDictionary(): Promise<DataDictionary>
	{
		if (this.promiseGetDataDictionary)
			return this.promiseGetDataDictionary as Promise<DataDictionary>;

		this.promiseGetDataDictionary = (async (): Promise<DataDictionary> =>
		{
			const result: DataDictionaryType | null =
				await this.internalGetServerFile<DataDictionaryType>('DataDictionaryMaster.json');

			if (!result)
			{
				// TODO: Handle critical situation if missing site data.
				return new DataDictionary({
					fields: [],
					validations: [],
					lookup: {},
					tables: {},
				});
			}

			return new DataDictionary(result);
		})();

		return this.promiseGetDataDictionary;
	}

	/**
	 Gets the list of users
	 @returns A promise for the users
	 */
	public async getUsers(forceRequest = false): Promise<User[]>
	{
		if (this.promiseGetUser && !forceRequest) return this.promiseGetUser as Promise<User[]>;

		this.promiseGetUser = (async (): Promise<User[]> =>
		{
			const result: User[] | null = await this.internalGetServerFile<User[]>('Users.json');

			if (!result)
			{
				return [];
			}

			return result;
		})();

		return this.promiseGetUser;
	}

	/**
	 Gets the list of roles
	 @returns A promise for the roles
	 */
	public async getRoles(forceRequest = false): Promise<Role[]>
	{
		if (this.promiseGetRole && !forceRequest) return this.promiseGetRole as Promise<Role[]>;

		this.promiseGetRole = (async (): Promise<Role[]> =>
		{
			const result: Role[] | null = await this.internalGetServerFile<Role[]>('Roles.json');

			if (!result)
			{
				return [];
			}

			return result;
		})();

		return this.promiseGetRole;
	}

	/**
	 Gets the list of groups
	 @returns A promise for the groups
	 */
	public async getGroups(forceRequest = false): Promise<Group[]>
	{
		if (this.promiseGetGroup && !forceRequest) return this.promiseGetGroup as Promise<Group[]>;

		this.promiseGetGroup = (async (): Promise<Group[]> =>
		{
			const result: Group[] | null = await this.internalGetServerFile<Group[]>('Groups.json');

			if (!result)
			{
				return [];
			}

			return result;
		})();

		return this.promiseGetGroup;
	}

	/**
	 Gets the list of membership
	 @returns A promise for the membership
	 */
	public async getMembership(forceRequest = false): Promise<Membership[]>
	{
		if (this.promiseGetMembership && !forceRequest)
			return this.promiseGetMembership as Promise<Membership[]>;

		this.promiseGetMembership = (async (): Promise<Membership[]> =>
		{
			const result: Membership[] | null = await this.internalGetServerFile<Membership[]>(
				'Memberships.json',
			);

			if (!result)
			{
				return [];
			}

			return result;
		})();

		return this.promiseGetMembership;
	}

	/**
	 Authorize a user given a token.
	 @param token A string value for token
	 @returns A promise for Auth Result
	 */
	public async authUserFromToken(token: string, isHubToken: boolean = false): Promise<AuthResult>
	{
		const axiosClient = this.internalGetAxios();

		try
		{
			const result = await axiosClient.post(isHubToken ? 'api/v1/auth/refresh-with-hub-token' : 'api/v1/auth/refresh', { token: token });
			console.log('DataServices Refresh Auth Token=', this.authToken);
			this.authToken = result.data.authToken;

			LogicEngine.currentUser = result.data.username;
			LogicEngine.currentIpAddress = result.data.ip;
			LogicEngine.timezoneOffset = new Date().getTimezoneOffset();
			LogicEngine.currentIsParticipant = false;

			if (/^PID/.test(token))
			{
				// console.log('FOUND PID:', result.data);
				LogicEngine.currentIsParticipant = true;
				LogicEngine.currentUser = '';
				LogicEngine.currentIpAddress = result.data.ip;
				if (result.data.uuid) LogicEngine.currentParticipantID = result.data.uuid;
			} else
			{
				// console.log('DID NOT MATCH:', token);
			}

			const groups: any[] = (result.data.role && result.data.role.groups) || [];

			// Update logicEngine with our current groups
			LogicEngine.currentGroups = groups.map((group) => group.name);

			return {
				isValidLogin: true,
				authToken: result.data.authToken,
				ipAddress: result.data.ip,
				role: result.data.role?.name,
				groups: groups.map((group) => group.name),
				username: result.data.username,
				firstName: result.data.firstName,
				lastName: result.data.lastName,
				participantId: result.data.participant_id,
			};
		} catch (err: any)
		{
			console.log('DataServices Auth Error:', err);
			return {
				isValidLogin: false,
				errorMessage: err.response.data.message,
			};
		}
	}

	/**
	 Authorize a user given username and password
	 @param username A string value for user name
	 @param password A string value for password
	 @param metadata A value for meta data
	 @returns A promise for Auth Result
	 */
	public async authUser(username: string, password: string, metadata?: any): Promise<AuthResult>
	{
		const axiosClient = this.internalGetAxios();

		try
		{
			const result = await axiosClient.post('api/v1/auth', { username, password, metadata });

			this.authToken = result.data.authToken;
			LogicEngine.currentUser = result.data.username;
			LogicEngine.currentIpAddress = result.data.ip;
			LogicEngine.currentParticipantID = '';
			LogicEngine.currentIsParticipant = false;
			LogicEngine.timezoneOffset = new Date().getTimezoneOffset();

			const groups: any[] = (result.data.role && result.data.role.groups) || [];

			// Update logicEngine with our current groups
			LogicEngine.currentGroups = groups.map((group) => group.name);
			LogicEngine.currentIsParticipant = false;

			return {
				isValidLogin: true,
				username: result.data.username,
				firstName: result.data.firstName,
				lastName: result.data.lastName,
				authToken: result.data.authToken,
				role: result.data.role?.name,
				ipAddress: result.data.ip,
				groups: groups.map((group) => group.name),
			};
		} catch (err: any)
		{
			return {
				isValidLogin: false,
				errorMessage: err.response?.data.message,
			};
		}
	}

	/**
	 Register participant
	 @param participant information about participant (see participant schema in trial server)
	 @returns A promise for Participant Register
	 */
	public async registerParticipant(participant: any): Promise<ParticipantRegisterResult>
	{
		const axiosClient = this.internalGetAxios();

		try
		{
			const result = await axiosClient.post('api/v1/participant/register', participant);

			this.authToken = result.data.authToken;
			console.log('Set Auth Token=', this.authToken);

			return {
				success: true,
				participantId: result.data.participantId,
			};
		} catch (err: any)
		{
			return {
				success: false,
				message: err.response?.data.message,
			};
		}
	}

	/**
	 Send message to a participant

	 @param participantId
	 @param messageText
	 @param messageBody
	 @param sendReason
	 @returns A promise for ParticipantSendMessageResult
	 */
	public async sendMessage(
		participantId: string,
		messageText: string,
		messageBody: string,
		sendReason: string,
	): Promise<ParticipantSendMessageResult>
	{
		const axiosClient = this.internalGetAxios();

		try
		{
			const result = await axiosClient.post(`api/v1/participant/${participantId}/message`, {
				messageText,
				messageBody,
				sendReason,
			});

			console.log('DataServices sendMessage response', result.data);

			return {
				success: true,
				publishId: result.data.publishId,
			};
		} catch (err: any)
		{
			return {
				success: false,
				message: err.response?.data.message || 'Something went wrong',
			};
		}
	}

	/**
	 Send message to a participant

	 @param stampParameters
	 @returns A promise for ParticipantSendMessageResult
	 */
	public async stampPdf(stampParameters: PdfStampParameter): Promise<StampPdfResult>
	{
		const axiosClient = this.internalGetAxios();

		try
		{
			const result = await axiosClient.post(`api/v1/utils/stampPdf`, stampParameters);

			console.log('DataServices sendMessage response', result.data);

			return {
				success: true,
				stampedFile: result.data.stampedFile,
			};
		} catch (err: any)
		{
			return {
				success: false,
				message: err.response?.data.message || 'Something went wrong',
			};
		}
	}

	/**
	 Send email to a participant

	 @param participantId
	 @param messageText
	 @param messageBody
	 @param sendReason
	 @returns A promise for ParticipantSendEmailResult
	 */
	public async sendEmail(
		participantId: string,
		messageText: string,
		messageBody: string,
		sendReason: string,
	): Promise<ParticipantSendEmailResult>
	{
		const axiosClient = this.internalGetAxios();

		try
		{
			const result = await axiosClient.post(`api/v1/participant/${participantId}/email`, {
				messageText,
				messageBody,
				sendReason,
			});

			console.log('DataServices sendEmail response', result.data);

			return {
				success: true,
				emailId: result.data.emailId,
			};
		} catch (err: any)
		{
			return {
				success: false,
				message: err.response?.data.message || 'Something went wrong',
			};
		}
	}

	/**
	 Send email to a site
	 @returns A promise for PSiteSendEmailResult
	 */
	public async sendSiteEmail(
		site_id: string,
		messageText: string,
		messageBody: string,
		sendReason: string,
	): Promise<ParticipantSendEmailResult>
	{
		const axiosClient = this.internalGetAxios();

		try
		{
			const result = await axiosClient.post(`api/v1/site/${site_id}/email`, {
				messageText,
				messageBody,
				sendReason,
			});

			console.log('DataServices sendSiteEmail response', result.data);

			return {
				success: true,
				emailId: result.data.emailId,
			};
		} catch (err: any)
		{
			return {
				success: false,
				message: err.response?.data.message || 'Something went wrong',
			};
		}
	}


	/**
	 Send sms to a participant

	 @param participantId
	 @param messageText
	 @param phoneNumber
	 @param sendReason
	 @returns A promise for ParticipantSendSmsResult
	 */
	public async sendSms(
		participantId: string,
		messageText: string,
		phoneNumber: string,
		sendReason: string,
	): Promise<ParticipantSendSmsResult>
	{
		const axiosClient = this.internalGetAxios();

		try
		{
			const result = await axiosClient.post(`api/v1/participant/${participantId}/sms`, {
				messageText,
				phoneNumber,
				sendReason,
			});

			console.log('DataServices sendSms response', result.data);

			return {
				success: true,
				smsId: result.data.smsId,
			};
		} catch (err: any)
		{
			return {
				success: false,
				message: err.response?.data.message || 'Something went wrong',
			};
		}
	}

	/**
	 Send message to a participant

	 @param siteId
	 @param participantId
	 @param template
	 @param subject
	 @param data
	 @param allow_dupe
	 @param to
	 @returns A promise for Participant Register
	 */
	public async sendSiteMessage(
		siteId: string,
		template: string,
		subject: string,
		data: any,
		allow_dupe: Boolean = false,
		participantId?: string,
		to?: string,
	): Promise<SiteSendMessageResult>
	{
		const axiosClient = this.internalGetAxios();

		try
		{
			const result = await axiosClient.post(`api/v1/site/${siteId}/email`, {
				template,
				subject,
				data,
				allow_dupe,
				participantId,
				to,
			});

			console.log('DataServices sendSiteMessage response', result.data);

			return {
				success: true,
				messageId: result.data.messageId,
			};
		} catch (err: any)
		{
			return {
				success: false,
				message: err.response?.data.message,
			};
		}
	}

	/**
	 Authorize a user given username and password
	 @param participantId A string value for participantId
	 @param password A string value for password
	 @param metadata A value for meta data
	 @returns A promise for Auth Result
	 */
	public async authParticipant(
		participantId: string,
		password: string,
		metadata?: any,
	): Promise<ParticipantAuthResult>
	{
		const axiosClient = this.internalGetAxios();

		try
		{
			const result = await axiosClient.post('api/v1/participant/auth', {
				participantId,
				password,
				metadata,
			});

			this.authToken = result.data.authToken;
			LogicEngine.currentIsParticipant = true;
			LogicEngine.currentParticipantID = participantId;
			LogicEngine.currentUser = '';
			LogicEngine.currentIpAddress = result.data.ip;
			LogicEngine.timezoneOffset = new Date().getTimezoneOffset();

			console.log('DataService Set Participant Auth Token=', this.authToken);

			return {
				success: true,
				authToken: result.data.authToken,
			};
		} catch (err: any)
		{
			return {
				success: false,
				errorMessage: err.response?.data.message,
			};
		}
	}

	/**
	 Set Password for Participant first time
	 @param participantId A string value for participantId
	 @param password A string value for password
	 @param metadata A value for meta data
	 @returns A promise for Auth Result
	 */
	public async participantSetPassword(
		participantId: string,
		password: string,
	): Promise<ParticipantAuthResult>
	{
		const axiosClient = this.internalGetAxios();

		try
		{
			const result = await axiosClient.post('api/v1/participant/auth/set-password', {
				participantId,
				password,
			});

			return {
				success: true,
			};
		} catch (err: any)
		{
			return {
				success: false,
				errorMessage: err.response?.data.message,
			};
		}
	}

	/**
	 Clear Password for Participant

	 @param participantId A string value for participantId
	 @returns A promise for Auth Result
	 */
	public async participantClearPassword(participantId: string): Promise<ParticipantAuthResult>
	{
		const axiosClient = this.internalGetAxios();

		try
		{
			await axiosClient.post('api/v1/participant/auth/clear-password', { participantId });

			return {
				success: true,
			};
		} catch (err: any)
		{
			return {
				success: false,
				errorMessage: err.response?.data.message,
			};
		}
	}

	/**
	 * Change the password for a user
	 * @params {string} oldPassword
	 * @params {string} newPassword
	 * @returns {Promise<string>} A promise for the result
	 */
	public async changePassword(
		oldPassword: string,
		newPassword: string,
	): Promise<ChangePasswordResult>
	{
		const axiosClient = this.internalGetAxios();

		try
		{
			await axiosClient.post('api/v1/auth/changePassword', { oldPassword, newPassword });
			return {
				success: true,
			};
		} catch (err: any)
		{
			console.log(err);
			return {
				success: false,
				message: err.response.data.message,
			};
		}
	}

	/**
	 * Change the password for a participant
	 * @params {string} oldPassword
	 * @params {string} newPassword
	 * @returns {Promise<string>} A promise for the result
	 */
	public async participantChangePassword(
		oldPassword: string,
		newPassword: string,
	): Promise<ChangePasswordResult>
	{
		const axiosClient = this.internalGetAxios();

		try
		{
			await axiosClient.post('api/v1/participant/auth/change-password', {
				oldPassword,
				newPassword,
			});
			return {
				success: true,
			};
		} catch (err: any)
		{
			console.log(err);
			return {
				success: false,
				message: err.response.data.message,
			};
		}
	}

	/**
	 * get login sessions for a username
	 * @param username A string value for user name
	 * @returns {Promise<any[]>} A promise for the result
	 */
	public async getLoginSessions(username?: string): Promise<any[]>
	{
		const axiosClient = this.internalGetAxios();

		try
		{
			const url = 'api/v1/auth/sessions' + (username ? `?username=${username}` : '');
			const result = await axiosClient.get(url);

			return result.data.sessions;
		} catch (err: any)
		{
			console.log(err);
			return [];
		}
	}

	/**
	 * Generate uuid from server
	 * @params {string} oldPassword
	 * @params {string} newPassword
	 * @returns {Promise<string>} A promise for the result
	 */
	public async generate3Dash4(): Promise<string>
	{
		const axiosClient = this.internalGetAxios();

		const response = await axiosClient.post('api/v1/utils/generate3Dash4', {});
		return response.data.uuid;
	}

	/**
	 * get login sessions for a username
	 * @param username A string value for user name
	 * @returns {Promise<any[]>} A promise for the result
	 */
	public async pingServer(): Promise<PingResult>
	{
		const axiosClient = this.internalGetAxios();

		const url = 'api/v1/system/ping';
		const result = await axiosClient.get(url);

		return result.data;
	}

	/**
	 * ping hub server
	 * @returns {Promise<PingHubResult>} A promise for the result
	 */
	public async pingHubServer(): Promise<PingHubResult>
	{
		const axiosHubClient = this.internalGetAxios(true);

		const url = 'api/v1/system/ping';
		const result = await axiosHubClient.get(url);

		return result.data;
	}

	/**
	 * validate participant api
	 *
	 * @param {string} participantId
	 * @returns {Promise<ValidateParticipantResult>} A promise for the result
	 */
	public async validateParticipantHubServer(
		participantId: string,
	): Promise<ValidateParticipantResult>
	{
		try
		{
			const axiosHubClient = this.internalGetAxios(true);

			const url = `api/v1/hub/validateParticipant/${participantId}`;
			const result = await axiosHubClient.get(url);

			return result.data;
		} catch (err: any)
		{
			return {
				success: false,
				errorMessage: err.response.data.message,
			};
		}
	}

	/**
	 * validate users api
	 *
	 * @param {string} username
	 * @returns {Promise<ValidateParticipantResult>} A promise for the result
	 */
	public async validateUserHubServer(username: string): Promise<ValidateUserResult>
	{
		try
		{
			const axiosHubClient = this.internalGetAxios(true);

			const url = `api/v1/hub/validateUser/${username}`;
			const result = await axiosHubClient.get(url);

			return result.data;
		} catch (err: any)
		{
			console.log(err);
			return {
				success: false,
				errorMessage: err.response.data.message,
			};
		}
	}

	/**
	 Send reset the password for a user
	 @param username A string value for user name
	 @returns A promise for the result
	 */
	public async resetPasswordRequest(username: string): Promise<boolean>
	{
		const axiosClient = this.internalGetAxios();

		try
		{
			const result = await axiosClient.post('api/v1/reset-password-request', { username });

			return true;
		} catch (err: any)
		{
			console.log(err);
			return false;
		}
	}

	/**
	 Send reset the password for a participant
	 @param participantId A string value for participantId
	 @returns A promise for the result
	 */
	public async participantResetPasswordRequest(participantId: string): Promise<boolean>
	{
		const axiosClient = this.internalGetAxios();

		try
		{
			const result = await axiosClient.post('api/v1/participant/reset-password-request', {
				participantId,
			});

			return true;
		} catch (err: any)
		{
			console.log(err);
			return false;
		}
	}

	/**
	 * Called any time Axios is used and there is an exception caught.
	 * Reports the exception and fires system event if needed.
	 */
	private internalHandleAxiosException(ex: any)
	{
		if (globalDebugApiCalls === 2) console.log('DataService:API Exception: ', ex);

		if (ex.response)
		{
			console.error(
				'DataServices: Exception in Axios reported, status code=',
				ex.response.status,
			);

			console.log('DataServices Exception Request:', ex.request?.path);

			if (ex.response?.data)
			{
				console.error('DataServices Exception Error:', ex.response.data);
			}

			if (ex.response.status === 401)
			{
				// Unauthorized / Bad Token / Bad User
				DataServices.evSystemProblem.emit({
					event_type: 'connection',
					event_text: ex.message,
					should_refresh_dispaly: false,
					should_relogin: true,
				});

				return;
			}

			if (ex.response.status === 400)
			{
				// Unauthorized / Bad Token / Bad User
				DataServices.evSystemProblem.emit({
					event_type: 'server_data',
					event_text: ex.message,
					should_refresh_dispaly: false,
					should_relogin: false,
				});

				return;
			}

			console.log('DataServices API Exception: response data=', ex.response.data);
			console.log('DataServices API Exception: response headers=', ex.response.headers);
		}

		if (ex.request)
		{
			console.log('DataServices API Exception: request headers=', ex.request.headers);
			console.log('DataServices API Exception: request data=', ex.request.data);
		}

		console.log('DataServices API Exception: message=', ex.message);
	}

	/**
	 * Change the password for a user
	 * @params {string} username
	 * @params {number} confirmationCode
	 * @params {string} newPassword
	 * @returns {Promise<string>} A promise for the result
	 */
	public async resetPasswordWithCode(
		username: string,
		confirmationCode: number,
		newPassword: string,
	): Promise<ChangePasswordResult>
	{
		const axiosClient = this.internalGetAxios();

		try
		{
			await axiosClient.post('api/v1/reset-password-with-code', {
				username,
				confirmationCode,
				newPassword,
			});
			return {
				success: true,
			};
		} catch (err: any)
		{
			console.log(err);
			return {
				success: false,
				message: err.response.data.message,
			};
		}
	}

	/**
	 * Change the password for a user
	 * @params {string} participantId
	 * @params {number} confirmationCode
	 * @params {string} newPassword
	 * @returns {Promise<string>} A promise for the result
	 */
	public async participantResetPasswordWithCode(
		participantId: string,
		confirmationCode: number,
		newPassword: string,
	): Promise<ChangePasswordResult>
	{
		const axiosClient = this.internalGetAxios();

		try
		{
			await axiosClient.post('api/v1/participant/reset-password-with-code', {
				participantId,
				confirmationCode,
				newPassword,
			});
			return {
				success: true,
			};
		} catch (err: any)
		{
			console.log(err);
			return {
				success: false,
				message: err.response.data.message,
			};
		}
	}

	/** Submit the attachment to the server.
	See AttachmentManager class for details on the data values.
	*/
	public async saveAttachment(
		data: AttachmentManager.AttachmentDataType,
	): Promise<AttachmentSaveResult>
	{
		const axiosClient = this.internalGetAxios();

		const formData = new FormData();
		formData.append('file', data.content_data, data.filename);

		try
		{
			const uploadResponse = await axiosClient.post('api/v1/attach/save', formData, {
				headers: {
					...formData.getHeaders(),
				},
			});

			delete data.content_data;
			// Set upload user from the token
			if (LogicEngine.currentUser)
			{
				data.upload_user = LogicEngine.currentUser;
			}
			if (LogicEngine.currentParticipantID)
			{
				data.participant_id = LogicEngine.currentParticipantID;
			}
			data.content_id = uploadResponse.data.id;
			// data.content_path = `/api/v1/attach/get/${data.id}`;
			data.file_size = uploadResponse.data.size;

			if (AttachmentManager.validate(data))
			{
				const dataRecord = new DataRecord('Attachments', data);
				await dataRecord.saveIfNeeded(true);
				return {
					success: true,
					id: data.id,
				};
			} else
			{
				return {
					success: false,
					message: 'Data validation failed',
				};
			}
		} catch (err: any)
		{
			console.log('Save Attachment request has failed', {
				apiCallName: 'saveAttachment',
				data: data,
				error: err,
			});

			return {
				success: false,
				message: err.message || 'Save Attachment request has failed',
			};
		}
	}

	/**
	 * Send confirmation code to phone number
	 * @params {string} phone
	 * @returns {Promise<GenericResult>} A promise for the result
	 */
	public async sendConfirmationCode(
		phone: string,
	): Promise<GenericResult>
	{
		const axiosClient = this.internalGetAxios();

		try
		{
			await axiosClient.post('api/v1/phone/send', {
				phone,
			});
			return {
				success: true,
			};
		} catch (err: any)
		{
			console.log('Dataservices:sendConfirmationCode Error:', err);
			return {
				success: false,
				message: err.response.data.message,
			};
		}
	}

	/**
	 * Verify phone confirmation code
	 * @params {string} phone
	 * @params {string} code
	 * @returns {Promise<ParticipantAuthResult>} A promise for the result
	 */
	public async verifyPhoneCode(
		phone: string,
		code: string,
	): Promise<ParticipantAuthResult>
	{
		const axiosClient = this.internalGetAxios();

		try
		{
			const result = await axiosClient.post('api/v1/phone/verify', {
				phone,
				code
			});
			return {
				success: result.data.success,
				authToken: result.data.authToken,
				user: result.data.user
			};
		} catch (err: any)
		{
			console.log('Dataservices:verifyPhoneCode Error:', err);
			return {
				success: false,
				message: err.response.data.message,
			};
		}
	}

	/**
	 * Make an API request to the server.
	 *
	 * @param {string} apiCallName Name of the API Call
	 * @param {object} data The object to send to the server.
	 * @param {string} method http method for the api call. By default it's GET
	 *
	 * @returns A promise with the ApiCallResult<T> where T is the type of object you expect back.
	 */
	public async makeApiCall<T>(
		apiCallName: string,
		data: any | null,
		method: HttpMethods = 'get',
	): Promise<ApiCallResult<T>>
	{
		const server = this.internalGetServerBase();

		const axiosOptions: any = {
			baseURL: server,
			timeout: 2000000,
			headers: {},
		};

		if (this.authToken)
		{
			axiosOptions.headers['x-auth-token'] = this.authToken;
		} else
		{
			if (!/cdn/.test(apiCallName))
				console.log('DataServices Warning: No authToken in makeApiCall ', apiCallName);
		}

		if (globalUseProxy)
		{
			axiosOptions.proxy = {
				host: '127.0.0.1',
				port: 8888,
			};
		}

		const instance = axios.create(axiosOptions);
		DataServices.debugAxiosCreate(instance);

		try
		{
			if (globalDebugApiCalls === 2)
				console.log('DataService:API Requesting API Call:', apiCallName, ' With ', data);

			let response;
			switch (method)
			{
				case 'get':
					response = await instance.get(apiCallName);
					break;
				case 'post':
					response = await instance.post(apiCallName, data);
					break;
				case 'put':
					response = await instance.put(apiCallName, data);
					break;
				case 'patch':
					response = await instance.patch(apiCallName, data);
					break;
				case 'delete':
					response = await instance.delete(apiCallName);
					break;
			}

			// if (globalDebugApiCalls === 2)
			// console.log("DataService:API Status=", response.status, "Raw=", response.data);

			// Verify the response code
			if (response && response.status === 200)
			{
				let result: ApiCallResult<T> = {
					statusCode: 200,
					isCached: false,
					isError: false,
					result: response.data as T,
				};

				this.writeResultToCache('api', apiCallName, data, result);
				return result;
			} else
			{
				if (response)
				{
					console.log('DataServices Invalid API Response', {
						apiCallName: apiCallName,
						data: data,
						method: method,
						resultCode: response.status,
						resultText: response.statusText,
						resultBody: response.data,
					});
				} else
				{
					console.log('DataServices Invalid API Response', {
						apiCallName,
						data,
						method,
						response,
					});
				}
			}
		} catch (ex: any)
		{
			this.internalHandleAxiosException(ex);
			if (ex.response?.status === 401)
			{
				return {
					statusCode: 0,
					isError: true,
					isCached: false,
					errorMessage: 'Exception, see console.',
				};
			}
		}

		// Try to read a cached version
		let result: ApiCallResult<T> = await this.readCachedResult('api', apiCallName, data);

		if (result && result.result)
		{
			result.isCached = true;
			return result;
		}

		result = {
			statusCode: 0,
			isError: true,
			isCached: false,
			errorMessage: 'Exception, see console.',
		};

		return result;
	}

	/**
	 Internal function that will make a request to the CDN to get a static
	 JSON content file.  If that request fails, an attempt will be made to
	 load that file from local storage instead.

	 @param filename [string] The name of the file on the CDN

	 @returns The data from the file or null upon critical failure.
	 */
	async internalGetCdnFile<T>(filename: string): Promise<T | null>
	{
		// Setting the Referer address is required for the Sharepoint CDN
		//

		const axiosOptions: any = {
			baseURL: this.internalGetCdnBase(),
			timeout: 60000,
			headers: {},
		};

		if (this.authToken)
		{
			axiosOptions.headers['x-auth-token'] = this.authToken;
		}

		if (globalUseProxy)
		{
			axiosOptions.proxy = {
				host: '127.0.0.1',
				port: 8888,
			};
		}

		const instance = axios.create(axiosOptions);
		DataServices.debugAxiosCreate(instance);

		try
		{
			if (globalDebugApiCalls === 2)
				console.log('DataService:API Requesting CDN File:', filename);

			const response = await instance.get(filename);

			if (globalDebugApiCalls === 2)
				console.log('DataService:API Status=', response.status, 'Raw=', response.data);

			// Verify the response code
			if (response.status === 200)
			{
				this.writeResultToCache('cdn', filename, null, response.data);
				return response.data as T;
			}

			// TODO Handle an error condition
		} catch (ex)
		{
			console.log('DataService:API Exception: ', ex);
			// TODO Handle an exception condition
		}

		let resultCache = await this.readCachedResult('cdn', filename, null);
		if (resultCache === null)
		{
			// TODO Handle no data from cache
			// Possibly log this error condition to a file or console
		} else
		{
			// TODO Handle a warning that this data is from a cached file.
			return resultCache;
		}

		return null;
	}

	/**
	 Internal function that will make a request to the CDN to get a static
	 JSON content file.  If that request fails, an attempt will be made to
	 load that file from local storage instead.

	 @param filename [string] The name of the file on the CDN

	 @returns The data from the file or null upon critical failure.
	 */
	async internalGetServerFile<T>(filename: string): Promise<T | null>
	{
		// Setting the Referer address is required for the Sharepoint CDN
		//

		const apiCallResult = await this.makeApiCall<T>(`cdn/${filename}`, null);
		return apiCallResult.result || null;
	}

	/**
	 * Read the results from a query that have been saved into the local cache.
	 *
	 * @param requestType The string value for request type
	 * @param queryText The queery to read
	 * @param queryData The value for query data
	 * @return A promise for api call result
	 */
	async readCachedResult(requestType: string, queryText: string, queryData?: any): Promise<any>
	{
		return new Promise((resolve, reject) =>
		{
			// console.log(`read this.internalQueryToHashKey("${requestType}", "${queryText}", "${queryData}");`);
			const key = this.internalQueryToHashKey(requestType, queryText, queryData);

			/**
			 * Try to find the result in the recent results cache
			 */
			if (this._recentCache[key])
			{
				if (this._recentCache[key].result)
				{
					resolve(this._recentCache[key]);
				} else
				{
					let result: ApiCallResult<any> = {
						statusCode: 200,
						isCached: true,
						isError: false,
						result: this._recentCache[key],
					};

					resolve(result);
				}
			}

			/*
			For the purpose of testing we can simulate having offline data by reading from a
			static file that is created and stored in the project.
			*/
			if (TestData.cache && TestData.cache[key])
			{
				let result: ApiCallResult<any> = {
					statusCode: 200,
					isCached: true,
					isError: false,
					result: TestData.cache[key],
				};

				resolve(result);
			} else
			{
				// console.error("readCachedResult missing for queryText=[", queryText, "] queryData=[", queryData, "] key=[" + key + "]");
			}

			resolve([]);
		});
	}

	/**
	 * Save the results from a query to local storage
	 *
	 * @param requestType The string value for a request type
	 * @param queryText The query to cache
	 * @param queryData The query data
	 * @param resultData The resulting data to save
	 * @return A promise for boolean
	 */
	async writeResultToCache(
		requestType: string,
		queryText: string,
		queryData: any,
		resultData: any,
	): Promise<boolean>
	{
		// console.log(`write this.internalQueryToHashKey("${requestType}", "${queryText}", "${queryData}");`);
		const key = this.internalQueryToHashKey(requestType, queryText, queryData);

		/** Save to memory cache */
		this._recentCache[key] = resultData;
		return true;
	}

	/**
	 * Internal function to strip out anything invalid from a query text
	 * this is like the slug() function.
	 *
	 * TODO Validate and fix this function.
	 *
	 * @param requestType A string value for a request type
	 * @param queryText Query text
	 * @param queryData A value for query data
	 * @return A string value for hash key
	 */
	internalQueryToHashKey(requestType: string, queryText: string, queryData: any): string
	{
		let strKey = requestType;
		strKey += '_' + queryText;

		if (queryData !== null) strKey += JSON.stringify(queryData);

		// console.log("internalQueryToHashKey(", strKey, ")");
		return sha1(strKey);
	}

	/**
	 * Initialize All Caches and AuthToken
	 *
	 * TODO:// Add more catches
	 *
	 */
	forceClearCacheAndLogout()
	{
		this._recentCache = {};
		this.cacheEdcPages = {};
		this.authToken = undefined;
		Participant.clearParticipantCache();
		TaskManager.clearTaskCache();
		DataQuery.resetCache();
	}
}
